import { Link } from 'react-router-dom'

import type { ScheduledAction } from '@/api/automation'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, LoadingBlock, type Column } from '@/design'
import type { DrillDown } from '@/lib/drilldown'
import { formatDateKey } from '@/lib/filters'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import './automation.css'
import { ScheduledStateBadge } from './badges'
import { useAutomationQuery, useCancelScheduledAction, useDrillDown } from './hooks'

/**
 * An absolute date and time: a due date can be in the future, so relative
 * time would misread as "just now". Both the date and the time are read in
 * UTC — the same basis `formatDateKey` uses — so they always describe the
 * same instant instead of the date drifting a day against a local time.
 */
function formatDueAt(iso: string): string {
  const time = new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  return `${formatDateKey(iso.slice(0, 10), true)}, ${time}`
}

function columns(
  href: (target: DrillDown) => string,
  cancel: (id: string) => void,
  cancellingId: string | null,
): Column<ScheduledAction>[] {
  return [
    { key: 'workflow', header: 'Workflow', sortValue: (row) => row.workflowName, cell: (row) => row.workflowName },
    { key: 'action', header: 'Action', cell: (row) => row.action.label },
    { key: 'due', header: 'Due', sortValue: (row) => row.dueAt, cell: (row) => formatDueAt(row.dueAt) },
    {
      key: 'for',
      header: 'For',
      cell: (row) => (row.entity ? <Link to={href(row.drillDown)}>{row.entity.label}</Link> : '—'),
    },
    { key: 'state', header: 'State', cell: (row) => <ScheduledStateBadge state={row.state} /> },
    {
      key: 'cancel',
      header: '',
      cell: (row) =>
        row.state === 'pending' ? (
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            disabled={cancellingId === row.id}
            onClick={() => cancel(row.id)}
          >
            Cancel
          </button>
        ) : null,
    },
  ]
}

/** Automation > Scheduled actions: the queue of actions due to run, with cancel for anything still pending. */
export function ScheduledPage() {
  const notLive = useNotLivePage('scheduledActions')
  const scheduled = useAutomationQuery('scheduled', (client) => client.scheduledActions())
  const cancel = useCancelScheduledAction()
  const href = useDrillDown()
  const search = useFilterSearch()

  const rows = scheduled.data?.data ?? []

  if (notLive) return <NotLivePage />

  return (
    <div className="ci-stack">
      <Card title="Scheduled actions" subtitle="Actions queued to run at a set time." busy={scheduled.isFetching}>
        {scheduled.isPending ? <LoadingBlock rows={6} /> : null}
        {scheduled.isError ? <EmptyState variant="outage" compact description="Scheduled actions could not be loaded." /> : null}
        {scheduled.data ? (
          <DataTable
            caption="Scheduled actions"
            columns={columns(href, (id) => cancel.mutate(id), cancel.isPending ? (cancel.variables ?? null) : null)}
            rows={rows}
            rowKey={(row) => row.id}
            rowHref={(row) => `/automation/workflows/${row.workflowId}${search}`}
            initialSort={{ key: 'due', direction: 'asc' }}
            empty={<EmptyState variant="no-data" compact title="Nothing scheduled" description="Actions queued by a running workflow will appear here." />}
          />
        ) : null}
      </Card>
    </div>
  )
}
